import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, Key, Lock, Loader2, QrCode, ArrowLeft, RefreshCw, AlertCircle, X } from 'lucide-react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTelegramAuth } from '../hooks';
import type { QrCodeData } from '../types';

type AuthMethod = 'phone' | 'qr';

interface AuthFlowProps {
  /** Callback when auth is complete (for add account flow) */
  onComplete?: () => void;
  /** Callback to cancel auth flow (for add account flow) */
  onCancel?: () => void;
}

export function AuthFlow({ onComplete, onCancel }: AuthFlowProps) {
  const {
    session,
    isLoading,
    isAuthorized,
    authStatus,
    startAuth,
    verifyCode,
    verify2FA,
    logout,
    startQrAuth,
    checkQrAuth,
    cancelQrAuth,
  } = useTelegramAuth();

  const [authMethod, setAuthMethod] = useState<AuthMethod>('qr');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  // QR state
  const [qrData, setQrData] = useState<QrCodeData | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const qrCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Флаг успешного сканирования QR (чтобы не показывать новый QR после сканирования)
  const qrScannedRef = useRef(false);
  // Локальное переопределение статуса авторизации (для немедленного UI обновления после QR сканирования)
  const [localAuthStatus, setLocalAuthStatus] = useState<string | null>(null);

  // Эффективный статус: используем локальный если установлен, иначе серверный
  const effectiveAuthStatus = localAuthStatus || authStatus;

  // Generate QR code image
  useEffect(() => {
    if (qrData?.token) {
      const qrUrl = `tg://login?token=${qrData.token}`;
      QRCode.toDataURL(qrUrl, {
        width: 280,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      }).then(setQrImageUrl).catch(console.error);
    } else {
      setQrImageUrl(null);
    }
  }, [qrData?.token]);

  // Auto-load QR if status is WAITING_QR but no qrData
  // НЕ запускаем если QR уже был успешно отсканирован (qrScannedRef)
  useEffect(() => {
    if (effectiveAuthStatus === 'WAITING_QR' && !qrData && !isLoading && !qrScannedRef.current) {
      startQrAuth().then((qr) => {
        if (qr) {setQrData(qr);}
      });
    }
  }, [effectiveAuthStatus, qrData, isLoading, startQrAuth]);

  // Start QR polling when QR is shown AND qr method is selected
  useEffect(() => {
    // Только если выбран QR метод
    if (authMethod !== 'qr') {return;}

    if (effectiveAuthStatus === 'WAITING_QR' && qrData) {
      // Poll every 3 seconds (reduced from 2 to avoid token expiration issues)
      qrCheckIntervalRef.current = setInterval(async () => {
        const result = await checkQrAuth();
        if (result?.status === 'AUTHORIZED' || result?.status === 'WAITING_PASSWORD') {
          // Помечаем что QR успешно отсканирован
          qrScannedRef.current = true;
          // Устанавливаем локальный статус СРАЗУ для немедленного обновления UI
          setLocalAuthStatus(result.status);
          // Stop polling
          if (qrCheckIntervalRef.current) {
            clearInterval(qrCheckIntervalRef.current);
            qrCheckIntervalRef.current = null;
          }
          setQrData(null);
        }
      }, 3000);

      // Refresh QR before expiration (только если QR ещё не отсканирован)
      const expiresIn = qrData.expires * 1000 - Date.now();
      const refreshTimeout = setTimeout(async () => {
        if (qrScannedRef.current) {return;} // Не обновляем если уже отсканирован
        const newQr = await startQrAuth();
        if (newQr) {
          setQrData(newQr);
        }
      }, Math.max(expiresIn - 5000, 1000)); // Refresh 5 seconds before expiration

      return () => {
        if (qrCheckIntervalRef.current) {
          clearInterval(qrCheckIntervalRef.current);
          qrCheckIntervalRef.current = null;
        }
        clearTimeout(refreshTimeout);
      };
    }
  }, [authMethod, effectiveAuthStatus, qrData, checkQrAuth, startQrAuth]);

  const handleStartAuth = async () => {
    if (!phoneNumber.trim()) {return;}
    await startAuth(phoneNumber);
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {return;}
    await verifyCode(code);
  };

  const handleVerify2FA = async () => {
    if (!password.trim()) {return;}
    await verify2FA(password);
  };

  const resetState = useCallback(() => {
    setPhoneNumber('');
    setCode('');
    setPassword('');
    setQrData(null);
    setQrImageUrl(null);
    setLocalAuthStatus(null); // Сбрасываем локальный статус
    qrScannedRef.current = false; // Сбрасываем флаг успешного сканирования
    if (qrCheckIntervalRef.current) {
      clearInterval(qrCheckIntervalRef.current);
      qrCheckIntervalRef.current = null;
    }
  }, []);

  const handleRestartAuth = async () => {
    await logout({ silent: true }); // Без тоста "Сессия завершена", logout сам сбросит session
    resetState();
    setAuthMethod('qr'); // Возвращаемся на начальный экран выбора способа
  };

  const handleStartQrAuth = async () => {
    const qr = await startQrAuth();
    if (qr) {
      setQrData(qr);
    }
  };

  const handleSwitchToPhone = () => {
    // Не вызываем cancelQrAuth - просто сбрасываем локальный стейт
    // QR токен истечёт сам по себе
    resetState();
    setAuthMethod('phone');
  };

  const handleSwitchToQr = () => {
    resetState();
    setAuthMethod('qr');
  };

  const handleCancel = async () => {
    await cancelQrAuth();
    resetState();
    onCancel?.();
  };

  // Authorized state - call onComplete if provided, or return null
  useEffect(() => {
    if (isAuthorized && session && onComplete) {
      onComplete();
    }
  }, [isAuthorized, session, onComplete]);

  if (isAuthorized && session) {
    return null;
  }

  // Expired session — прошлая авторизация протухла, нужна повторная
  if (effectiveAuthStatus === 'EXPIRED') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Сессия истекла
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Предыдущая авторизация Telegram больше не активна
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {session?.telegramPhone
                  ? `Аккаунт ${session.telegramPhone} был отключён. Авторизуйтесь заново.`
                  : 'Сессия была отключена. Авторизуйтесь заново.'}
              </AlertDescription>
            </Alert>
            <Button onClick={handleRestartAuth} disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Авторизоваться заново
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (effectiveAuthStatus === 'ERROR') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Ошибка авторизации
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Произошла ошибка при авторизации в Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Авторизация не удалась. Попробуйте начать заново.
              </AlertDescription>
            </Alert>
            <Button onClick={handleRestartAuth} disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Начать заново
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Waiting for 2FA password
  if (effectiveAuthStatus === 'WAITING_PASSWORD') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Двухфакторная аутентификация
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Введите пароль для двухфакторной аутентификации
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Пароль 2FA"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify2FA()}
              disabled={isLoading}
              autoFocus
            />
            <Button onClick={handleVerify2FA} disabled={isLoading || !password.trim()} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              Подтвердить
            </Button>
            <Button variant="ghost" onClick={handleRestartAuth} disabled={isLoading} className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Начать заново
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Waiting for verification code
  if (effectiveAuthStatus === 'WAITING_CODE') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Введите код
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Мы отправили код в ваше приложение Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Код из Telegram"
              value={code}
              onChange={(e) => setCode(e.target.value.replaceAll(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
              maxLength={6}
              disabled={isLoading}
              autoFocus
            />
            <Button onClick={handleVerifyCode} disabled={isLoading || !code.trim()} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Key className="mr-2 h-4 w-4" />
              )}
              Подтвердить
            </Button>
<Button variant="ghost" onClick={handleRestartAuth} disabled={isLoading} className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Начать заново
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Waiting for QR scan (only if QR method is selected)
  if ((effectiveAuthStatus === 'WAITING_QR' && authMethod === 'qr') || (qrData && authMethod === 'qr')) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Войти по QR-коду
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Отсканируйте код в приложении Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-center p-4 bg-white rounded-lg">
              {qrImageUrl ? (
                <img src={qrImageUrl} alt="QR Code" className="w-[280px] h-[280px]" />
              ) : (
                <div className="w-[280px] h-[280px] flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Откройте Telegram на телефоне</li>
              <li>Перейдите в Настройки → Устройства → Подключить устройство</li>
              <li>Наведите камеру на QR-код</li>
            </ol>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleStartQrAuth} disabled={isLoading} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                Обновить
              </Button>
              <Button variant="outline" onClick={handleSwitchToPhone} disabled={isLoading} className="flex-1">
                <Phone className="mr-2 h-4 w-4" />
                По телефону
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Initial state - choose auth method
  if (authMethod === 'qr') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Подключить Telegram
            </span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            Быстрый вход через QR-код
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button onClick={handleStartQrAuth} disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <QrCode className="mr-2 h-4 w-4" />
              )}
              Показать QR-код
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">или</span>
              </div>
            </div>
            <Button variant="outline" onClick={handleSwitchToPhone} disabled={isLoading} className="w-full">
              <Phone className="mr-2 h-4 w-4" />
              Войти по номеру телефона
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Phone number auth
  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Подключить Telegram
          </span>
          {onCancel && (
            <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          Введите номер телефона вашего Telegram аккаунта для авторизации
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Input
            type="tel"
            placeholder="+7 999 123 45 67"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleStartAuth()}
            disabled={isLoading}
            autoFocus
          />
          <Button onClick={handleStartAuth} disabled={isLoading || !phoneNumber.trim()} className="w-full">
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Phone className="mr-2 h-4 w-4" />
            )}
            Получить код
          </Button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">или</span>
            </div>
          </div>
          <Button variant="outline" onClick={handleSwitchToQr} disabled={isLoading} className="w-full">
            <QrCode className="mr-2 h-4 w-4" />
            Войти по QR-коду
          </Button>
          <p className="text-xs text-muted-foreground">
            Для работы с чатами необходимо авторизоваться в Telegram.
            Код будет отправлен в ваше приложение Telegram.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
